import React, { useEffect, useState, useRef } from 'react';
import Button from '../../../shared/components/ui/Button';
import { videoHighlightsService, VideoHighlight } from '../../../shared/services/videoHighlightsService';
import { VideoProcessingJob } from '../../../shared/services/videoProcessingJobsService';
import { scoresheetService } from '../../../shared/services/scoresheetService';
import { useAuth } from '../../../shared/contexts/AuthContext';
import EditHighlightModal from '../components/EditHighlightModal';
import ProcessHighlightModal from '../components/ProcessHighlightModal';
import HighlightDetailsDrawer from '../components/HighlightDetailsDrawer';
import DeleteHighlightModal from '../components/DeleteHighlightModal';

type MatchPlayer = { player_id: string; full_name: string; kit_number: number | null; team_id: string; team_name: string };

interface HighlightsTabProps {
  matchId: string;
  isActive?: boolean;
  refreshKey?: number;
  onOpenHighlight: (highlightId: string) => void;
  onHighlightsChanged?: () => void;
  onToast?: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

const statusClasses: Record<string, string> = {
  saved: 'bg-gray-100 text-gray-700',
  queued: 'bg-yellow-100 text-yellow-700',
  processing: 'bg-blue-100 text-blue-700',
  stitched: 'bg-purple-100 text-purple-700',      // NEW: Base video ready
  enhancing: 'bg-indigo-100 text-indigo-700',     // NEW: Overlay worker running
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
};

const HighlightsTab: React.FC<HighlightsTabProps> = ({
  matchId,
  isActive = true,
  refreshKey = 0,
  onOpenHighlight,
  onHighlightsChanged,
  onToast,
}) => {
  const { user } = useAuth();
  const [highlights, setHighlights] = useState<VideoHighlight[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [processTarget, setProcessTarget] = useState<VideoHighlight | null>(null);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [editHighlight, setEditHighlight] = useState<VideoHighlight | null>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [selectedHighlightForDetails, setSelectedHighlightForDetails] = useState<VideoHighlight | null>(null);
  const [deleteHighlightData, setDeleteHighlightData] = useState<{ highlightId: string; highlightName: string; hasStitched: boolean; hasEnhanced: boolean } | null>(null);
  const [isDeleteHighlightModalOpen, setIsDeleteHighlightModalOpen] = useState(false);
  const pollingIntervalRef = useRef<number | null>(null);
  const [overlayJobStatuses, setOverlayJobStatuses] = useState<Record<string, 'not_run' | 'failed' | 'succeeded' | 'processing'>>({});
  const [matchPlayers, setMatchPlayers] = useState<MatchPlayer[]>([]);

  const loadHighlights = async (showLoading = true) => {
    if (!matchId) return;
    if (showLoading) {
      setLoading(true);
      setError(null);
    }
    const result = await videoHighlightsService.getHighlightsByMatchId(matchId);
    if (result.success && result.data) {
      setHighlights(result.data);
      // Track which highlights need polling
      // Also poll for enhancing status
      const needsPolling = result.data.filter(
        (h) => ['queued', 'processing', 'enhancing'].includes(h.status)
      );
      setProcessingIds(new Set(needsPolling.map((h) => h.id)));
      
      // Load overlay job statuses for highlights with overlay_config
      loadOverlayJobStatuses(result.data);
    } else {
      if (showLoading) {
        setHighlights([]);
        setError(result.error || 'Failed to load highlights');
      }
    }
    if (showLoading) {
      setLoading(false);
    }
  };

  // Load overlay job statuses for highlights with overlay_config or stitched status
  // This helps determine if "Send for processing" button should be shown
  const loadOverlayJobStatuses = async (highlights: VideoHighlight[]) => {
    // Check highlights with overlay_config OR stitched status (may have failed overlay job)
    const highlightsToCheck = highlights.filter(
      (h) => (h.overlay_config && 
              h.overlay_config !== '{}' && 
              h.overlay_config !== null) ||
             h.status === 'stitched'
    );

    const statusMap: Record<string, 'not_run' | 'failed' | 'succeeded'> = {};
    
    // Fetch jobs for all relevant highlights in parallel
    const jobPromises = highlightsToCheck.map(async (highlight) => {
      const jobsResult = await videoHighlightsService.getHighlightJobs(highlight.id);
      if (jobsResult.success && jobsResult.data) {
        const overlayJob = jobsResult.data.find(
          (job: VideoProcessingJob) => job.job_type === 'video_overlay_enhancements'
        );
        
        if (overlayJob) {
          if (overlayJob.status === 'succeeded') {
            statusMap[highlight.id] = 'succeeded';
          } else if (overlayJob.status === 'failed') {
            statusMap[highlight.id] = 'failed';
          } else {
            // queued, running - still processing
            statusMap[highlight.id] = 'not_run';
          }
        } else {
          // No overlay job exists yet
          statusMap[highlight.id] = 'not_run';
        }
      }
    });

    await Promise.all(jobPromises);
    setOverlayJobStatuses(statusMap);
  };

  useEffect(() => {
    if (isActive) {
      loadHighlights();
    }
    // Cleanup polling on unmount
    return () => {
      if (pollingIntervalRef.current) {
        window.clearInterval(pollingIntervalRef.current);
      }
    };
  }, [isActive, matchId, refreshKey]);

  // Load match lineup players for resolving player_ids to names in rows and drawer
  useEffect(() => {
    if (!isActive || !matchId) return;
    let cancelled = false;
    scoresheetService.getMatchLineupPlayers(matchId).then((players) => {
      if (!cancelled) setMatchPlayers(players);
    }).catch(() => {
      if (!cancelled) setMatchPlayers([]);
    });
    return () => { cancelled = true; };
  }, [isActive, matchId]);

  const playerIdToName = (playerIds: string[] | null | undefined): string => {
    if (!playerIds?.length) return '';
    const byId = new Map(matchPlayers.map((p) => [p.player_id, p.full_name]));
    const names = playerIds.map((id) => byId.get(id) || 'Unknown player');
    return names.join(', ');
  };

  // Poll for status updates on queued/processing highlights
  useEffect(() => {
    if (processingIds.size === 0) {
      if (pollingIntervalRef.current) {
        window.clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    // Poll every 5 seconds (silently, without showing loading state)
    pollingIntervalRef.current = window.setInterval(() => {
      loadHighlights(false); // Don't show loading state during polling
    }, 5000);

    return () => {
      if (pollingIntervalRef.current) {
        window.clearInterval(pollingIntervalRef.current);
      }
    };
  }, [processingIds.size]);

  const handleSaveEdit = async (updates: { name: string; player_ids?: string[] | null }) => {
    if (!user?.id || !editHighlight) return;
    setIsSavingEdit(true);
    const result = await videoHighlightsService.updateHighlight(editHighlight.id, {
      name: updates.name,
      ...(updates.player_ids !== undefined && { player_ids: updates.player_ids }),
      updatedBy: user.id,
    });
    setIsSavingEdit(false);
    if (!result.success) {
      onToast?.(result.error || 'Failed to update highlight', 'error');
      return;
    }
    onToast?.('Highlight updated successfully', 'success');
    setEditHighlight(null);
    // Reload highlights to reflect any is_active changes
    await loadHighlights();
    onHighlightsChanged?.();
  };

  const handleDeleteHighlight = (highlight: VideoHighlight) => {
    const hasStitched = Boolean(highlight.output_storage_path || highlight.output_url);
    const hasEnhanced = Boolean(highlight.enhanced_storage_path || highlight.enhanced_url);
    setDeleteHighlightData({
      highlightId: highlight.id,
      highlightName: highlight.name,
      hasStitched,
      hasEnhanced,
    });
    setIsDeleteHighlightModalOpen(true);
  };

  const handleDeleteHighlightSuccess = async () => {
    await loadHighlights();
    onHighlightsChanged?.();
    setIsDeleteHighlightModalOpen(false);
    setDeleteHighlightData(null);
  };

  const handleProcess = async (highlight: VideoHighlight) => {
    if (!user?.id) {
      onToast?.('Please sign in to process highlights.', 'error');
      return;
    }
    // Allow processing saved (stitch) and stitched (overlays)
    if (!['saved', 'stitched'].includes(highlight.status)) {
      onToast?.(`Cannot process highlight. Current status: ${highlight.status}`, 'error');
      return;
    }
    setIsProcessingAction(true);
    setProcessingIds((prev) => new Set([...prev, highlight.id]));
    const result = await videoHighlightsService.processHighlight(highlight.id);
    setIsProcessingAction(false);
    setProcessTarget(null);
    if (!result.success) {
      onToast?.(result.error || 'Failed to start processing', 'error');
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(highlight.id);
        return next;
      });
      return;
    }
    onToast?.('Highlight queued for processing', 'success');
    await loadHighlights();
    onHighlightsChanged?.();
  };


  if (loading) {
    return (
      <div className="h-[400px] flex items-center justify-center bg-white border border-gray-200 rounded-lg">
        <p className="text-sm text-gray-500">Loading highlights…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-[400px] flex items-center justify-center bg-white border border-red-200 rounded-lg">
        <div className="text-center">
          <p className="text-sm text-red-600 mb-2">{error}</p>
          <Button onClick={loadHighlights} variant="outline" size="sm">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (highlights.length === 0) {
    return (
      <div className="h-[400px] flex items-center justify-center bg-white border border-gray-200 rounded-lg">
        <div className="text-center max-w-md">
          <p className="text-gray-600 text-base font-medium">No highlights saved yet</p>
          <p className="text-gray-400 text-sm mt-1">
            Build a timeline in the Video Editor tab and click “Save Highlight” to see it listed here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {highlights.map((highlight) => (
        <div
          key={highlight.id}
          className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors cursor-pointer"
          onClick={() => setSelectedHighlightForDetails(highlight)}
        >
          <div className="flex items-center justify-between">
            {/* Left Side - Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-base font-semibold text-gray-900 truncate">
                  {highlight.name}
                </h3>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                    statusClasses[highlight.status] || 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {highlight.status.charAt(0).toUpperCase() + highlight.status.slice(1)}
                </span>
              </div>
              {(() => {
                const playerNames = playerIdToName(highlight.player_ids);
                if (playerNames) {
                  return (
                    <p className="text-xs text-gray-600 mb-1">
                      Players: {playerNames}
                    </p>
                  );
                }
                if (highlight.player_ids && highlight.player_ids.length === 0) {
                  return (
                    <p className="text-xs text-gray-500 mb-1">Team highlight</p>
                  );
                }
                return null;
              })()}
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span>{highlight.segments_data?.length || 0} clips</span>
                <span>•</span>
                <span>Saved {new Date(highlight.updated_at).toLocaleDateString()}</span>
                {highlight.overlay_config && 
                 highlight.overlay_config !== '{}' && 
                 highlight.overlay_config !== null && (() => {
                  // Determine overlay job status
                  // Priority: fetched job status > inferred from highlight status
                  let overlayStatus: 'not_run' | 'failed' | 'succeeded' | 'processing' = overlayJobStatuses[highlight.id];
                  
                  // Fallback: infer from highlight status if job status not loaded yet
                  if (!overlayStatus) {
                    if (highlight.status === 'completed') {
                      overlayStatus = 'succeeded'; // Overlay must have succeeded for completed status
                    } else if (highlight.status === 'failed') {
                      // Could be stitch failure or overlay failure - default to 'not_run' until jobs load
                      // If overlay job exists and failed, overlayJobStatuses will have 'failed'
                      overlayStatus = 'not_run';
                    } else {
                      overlayStatus = 'not_run'; // saved, stitched, queued, processing, enhancing
                    }
                  }
                  
                  // Determine styling based on status
                  let badgeClasses = 'text-xs px-2 py-0.5 rounded border bg-transparent';
                  if (overlayStatus === 'succeeded') {
                    badgeClasses += ' border-green-500 text-green-700';
                  } else if (overlayStatus === 'failed') {
                    badgeClasses += ' border-red-500 text-red-700';
                  } else {
                    // not_run or processing
                    badgeClasses += ' border-gray-300 text-gray-600';
                  }
                  
                  return (
                    <>
                      <span>•</span>
                      <span className={badgeClasses}>
                        Overlay Configs
                      </span>
                    </>
                  );
                })()}
                {['queued', 'processing', 'enhancing'].includes(highlight.status) && (
                  <>
                    <span>•</span>
                    <span className="text-blue-600">
                      {highlight.status === 'queued' && 'In queue...'}
                      {highlight.status === 'processing' && 'Processing...'}
                      {highlight.status === 'enhancing' && 'Adding overlays...'}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Right Side - Actions */}
            <div
              className="flex items-center space-x-1 ml-4 flex-shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Process Button - For saved (stitch) and stitched (overlays) highlights */}
              {(() => {
                // Don't show "Send for processing" if:
                // 1. Status is 'stitched' AND overlay job already exists (even if failed) - user should retry via drawer
                // 2. Status is 'stitched' AND no overlay_config exists - nothing to process
                // 3. Status is not 'saved' or 'stitched'
                // 4. Already processing
                
                if (!['saved', 'stitched'].includes(highlight.status)) {
                  return null;
                }
                
                // For 'stitched' status: only show button if overlay_config exists AND no overlay job exists yet
                if (highlight.status === 'stitched') {
                  // Check if overlay_config exists
                  const hasOverlayConfig = highlight.overlay_config && 
                    highlight.overlay_config !== '{}' && 
                    highlight.overlay_config !== null;
                  
                  // If no overlay config, don't show button - nothing to process
                  if (!hasOverlayConfig) {
                    return null;
                  }
                  
                  // Check overlay job status
                  const overlayJobStatus = overlayJobStatuses[highlight.id];
                  // If overlay job status is undefined, we haven't loaded it yet - don't show button to avoid flicker
                  // If overlay job exists (failed, succeeded, or processing), don't show button - user should retry via drawer
                  // Only show button if we've explicitly confirmed no overlay job exists (status === 'not_run')
                  if (overlayJobStatus === undefined || overlayJobStatus !== 'not_run') {
                    return null;
                  }
                }
                
                // Determine tooltip text based on status and configs
                let tooltipText = 'Send for processing';
                if (processingIds.has(highlight.id)) {
                  tooltipText = 'Processing...';
                } else if (highlight.status === 'stitched') {
                  tooltipText = 'Send for overlays';
                } else if (highlight.status === 'saved') {
                  // Check if both configs exist
                  const hasSegmentEffects = highlight.segment_effects_config && 
                    highlight.segment_effects_config !== '{}' && 
                    highlight.segment_effects_config !== null;
                  const hasOverlayConfig = highlight.overlay_config && 
                    highlight.overlay_config !== '{}' && 
                    highlight.overlay_config !== null;
                  
                  if (hasSegmentEffects && hasOverlayConfig) {
                    tooltipText = 'Send for processing';
                  } else {
                    tooltipText = 'Send for stitching';
                  }
                }
                
                return (
                <button
                  onClick={() => setProcessTarget(highlight)}
                  disabled={processingIds.has(highlight.id)}
                  className={`p-1.5 transition-colors rounded ${
                    processingIds.has(highlight.id)
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-blue-600 hover:bg-blue-100'
                  }`}
                    title={tooltipText}
                  >
                    {/* Play button in circle icon (same as capture app) */}
                    {processingIds.has(highlight.id) ? (
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                  </button>
                );
              })()}

              {/* Open Storage Link Button */}
              {(() => {
                // Prefer enhanced URL/path, fallback to base
                const videoUrl = highlight.enhanced_url || highlight.output_url;
                const storagePath = highlight.enhanced_storage_path || highlight.output_storage_path;
                const hasVideo = videoUrl || storagePath;
                
                if (!hasVideo) return null;
                
                return (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Prefer URL, fallback to constructing from storage path
                      const url = videoUrl || (storagePath ? `https://cdn.capture.kridah.com/${storagePath}` : null);
                      if (url) {
                        window.open(url, '_blank');
                      }
                    }}
                    className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="Open storage link"
                  >
                    {/* Arrow icon (external link) */}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </button>
                );
              })()}

              {/* Edit Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditHighlight(highlight);
                }}
                className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                title="Edit highlight"
              >
                {/* Edit icon */}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>

              {/* Delete Button - Always show; for "saved" (no videos) deletes record only */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteHighlight(highlight);
                }}
                className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                title="Delete highlight"
              >
                {/* Trash icon */}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>

              {/* View Details Button - Eye Icon */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedHighlightForDetails(highlight);
                }}
                className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                title="View details"
              >
                {/* Eye icon */}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              </button>

              {/* Open in Video Editor Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenHighlight(highlight.id);
                }}
                className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                title="Open in video editor"
              >
                {/* Film reel / editor icon */}
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <rect x="4" y="5" width="10" height="14" rx="2" ry="2" strokeWidth="2" />
                  <circle cx="9" cy="9" r="1" fill="currentColor" />
                  <circle cx="9" cy="15" r="1" fill="currentColor" />
                  <path d="M16 7l3-2v14l-3-2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      ))}

      <EditHighlightModal
        isOpen={!!editHighlight}
        highlight={editHighlight}
        isSaving={isSavingEdit}
        onClose={() => {
          if (isSavingEdit) return;
          setEditHighlight(null);
        }}
        onSave={handleSaveEdit}
      />

      <ProcessHighlightModal
        isOpen={!!processTarget}
        highlightName={processTarget?.name || ''}
        isProcessing={isProcessingAction}
        onClose={() => {
          if (isProcessingAction) return;
          setProcessTarget(null);
        }}
        onConfirm={() => {
          if (processTarget) {
            void handleProcess(processTarget);
          }
        }}
      />

      <HighlightDetailsDrawer
        isOpen={!!selectedHighlightForDetails}
        highlight={selectedHighlightForDetails}
        matchPlayers={matchPlayers}
        onClose={() => setSelectedHighlightForDetails(null)}
        onRefresh={() => {
          loadHighlights();
          onHighlightsChanged?.();
        }}
        onToast={onToast}
        onEditHighlight={(highlightId) => {
          const highlight = highlights.find(h => h.id === highlightId);
          if (highlight) {
            setEditHighlight(highlight);
            setSelectedHighlightForDetails(null);
          }
        }}
        onDeleteHighlight={(highlightId, highlightName) => {
          const highlight = highlights.find(h => h.id === highlightId);
          if (highlight) {
            handleDeleteHighlight(highlight);
          }
        }}
      />

      {/* Delete Highlight Modal */}
      {deleteHighlightData && (
        <DeleteHighlightModal
          isOpen={isDeleteHighlightModalOpen}
          onClose={() => {
            setIsDeleteHighlightModalOpen(false);
            setDeleteHighlightData(null);
          }}
          highlightId={deleteHighlightData.highlightId}
          highlightName={deleteHighlightData.highlightName}
          hasStitched={deleteHighlightData.hasStitched}
          hasEnhanced={deleteHighlightData.hasEnhanced}
          userId={user?.id}
          onSuccess={handleDeleteHighlightSuccess}
          onToast={onToast}
        />
      )}
    </div>
  );
};

export default HighlightsTab;

